import { Navigate, Outlet } from 'react-router-dom';

import FetchingErrorBoundary from 'components/FetchingErrorBoundary';
import Home from 'components/Home';
import LoginContainer from 'components/LoginContainer';
import { COMPANY } from 'models/company.model';
import CertificateDetailsPage from 'pages/CertificateDetailsPage';
import CertificatesPage from 'pages/CertificatesPage';
import ConnectionTreePage from 'pages/ConnectionTreePage';
import DraftDetailsPage from 'pages/DraftDetailsPage';
import LoginOptionsPage from 'pages/LoginOptionsPage';
import LoginPage from 'pages/LoginPage';
import NewCertPage from 'pages/NewCertPage';
import NewCertTypePage from 'pages/NewCertTypePage';
import NewPrivateDocumentPage from 'pages/NewPrivateDocumentPage';
import PrivateDocumentDetails from 'pages/PrivateDocumentDetails';
import PurchasingDepartmentsPage from 'pages/PurchasingDepartmentsPage';
import SalesDepartmentsPage from 'pages/SalesDepartmentsPage';
import TestResultsPage from 'pages/TestResultsPage';

// import NewInvoicePage from 'pages/NewInvoicePage';
// import NewOrderPage from 'pages/NewOrderPage';
// import NewQuotePage from 'pages/NewQuotePage';
// import NewRFQPage from 'pages/NewRFQPage';

// import OrderDetailsPage from 'pages/OrderDetailsPage';
// import PurchasingDepartmentPage from 'pages/PurchasingDepartmentPage';
// import InvoicesReceived from 'pages/PurchasingDepartmentPage/InvoicesReceived';
// import OrdersIssued from 'pages/PurchasingDepartmentPage/OrdersIssued';
// import QuotesReceived from 'pages/PurchasingDepartmentPage/QuotesReceived';
// import RFQIssued from 'pages/PurchasingDepartmentPage/RFQIssued';
// import QuoteDetailsPage from 'pages/QuoteDetailsPage';
// import RFQDetailsPage from 'pages/RFQDetailsPage';
// import InvoicesIssued from 'pages/SalesDepartmentPage/InvoicesIssued';
// import OrdersReceived from 'pages/SalesDepartmentPage/OrdersReceived';
// import QuotesIssued from 'pages/SalesDepartmentPage/QuotesIssued';
// import RFQReceived from 'pages/SalesDepartmentPage/RFQReceived';
// import SalesDepartmentPage from 'pages/SalesDepartmentPage/SalesDepartmentPage';
// import AnalyticsPage from 'pages/AnalyticsPage/AnalyticsPage';
// import InvoiceDetailsPage from 'pages/InvoiceDetailsPage';

export const redirectPaths = {
  loggedOut: '/login',
  [COMPANY.CHEMISTRY]: '/home/certificates',
  [COMPANY.BATTERY_CELLS]: '/home/certificates',
  [COMPANY.BATTERY_PACKS]: '/home/certificates',
  [COMPANY.BATTERY_TECH_REVIEW]: '/home/certificates',
  [COMPANY.BATTERY_PACKS_REPAIR]: '/home/certificates',
  [COMPANY.BATTERY_REPURPOSE]: '/home/certificates',
  [COMPANY.BATTERY_PACKS_RECYCLING]: '/home/certificates',
  [COMPANY.BATTERY_WASTE]: '/home/certificates',
  [COMPANY.AUTHORITIES]: '/home/certificates',
};

export type RouteModel = {
  path: string;
  element: React.ReactElement;
  children?: RouteModel[];
  restrictedTo?: COMPANY[];
};

const allCompanies = Object.values(COMPANY) as COMPANY[];

// const documentRestricted = [COMPANY.BATTERY_PACKS, COMPANY.CHEMISTRY];

export const routes: RouteModel[] = [
  {
    path: '/login',
    element: <LoginContainer />,
    children: [
      {
        path: '',
        element: <Outlet />,
        children: [
          {
            path: '',
            element: <LoginPage />,
          },
          {
            path: ':company',
            element: <LoginOptionsPage />,
          },
        ],
      },
    ],
  },
  {
    path: '/home',
    element: <Home />,
    restrictedTo: allCompanies,
    children: [
      {
        path: '',
        element: <Navigate to="certificates" />,
        restrictedTo: allCompanies,
      },
      {
        path: 'certificates',
        element: <Outlet />,
        restrictedTo: allCompanies,
        children: [
          {
            path: '',
            element: (
              <FetchingErrorBoundary>
                <CertificatesPage />
              </FetchingErrorBoundary>
            ),
            restrictedTo: allCompanies,
          },
          {
            path: ':id',
            element: <Outlet />,
            restrictedTo: allCompanies,
            children: [
              {
                path: '',
                element: (
                  <FetchingErrorBoundary>
                    <CertificateDetailsPage />
                  </FetchingErrorBoundary>
                ),
                restrictedTo: allCompanies,
              },
              {
                path: 'certificate-structure',
                element: (
                  <FetchingErrorBoundary>
                    <ConnectionTreePage />
                  </FetchingErrorBoundary>
                ),
                restrictedTo: allCompanies,
              },
            ],
          },
        ],
      },
      {
        path: 'draft/:id',
        element: (
          <FetchingErrorBoundary>
            <DraftDetailsPage />
          </FetchingErrorBoundary>
        ),
        restrictedTo: allCompanies,
      },
      {
        path: 'new-certificate',
        element: <NewCertTypePage />,
        restrictedTo: (Object.values(COMPANY) as COMPANY[]).filter(
          (c) => c !== COMPANY.AUTHORITIES
        ),
      },
      {
        path: 'new-certificate/:category',
        element: <NewCertPage />,
        restrictedTo: allCompanies,
      },
      {
        path: 'test-results',
        element: <Outlet />,
        restrictedTo: [
          COMPANY.CHEMISTRY,
          COMPANY.BATTERY_CELLS,
          COMPANY.BATTERY_PACKS,
          COMPANY.AUTHORITIES,
        ],
        children: [
          {
            path: '',
            element: <TestResultsPage />,
            restrictedTo: [
              COMPANY.CHEMISTRY,
              COMPANY.BATTERY_CELLS,
              COMPANY.BATTERY_PACKS,
              COMPANY.AUTHORITIES,
            ],
          },
          {
            path: 'new/:category',
            element: <NewPrivateDocumentPage />,
            restrictedTo: [COMPANY.CHEMISTRY, COMPANY.BATTERY_CELLS, COMPANY.BATTERY_PACKS],
          },
          {
            path: ':id',
            element: (
              <FetchingErrorBoundary>
                <PrivateDocumentDetails />
              </FetchingErrorBoundary>
            ),
            restrictedTo: [
              COMPANY.CHEMISTRY,
              COMPANY.BATTERY_CELLS,
              COMPANY.BATTERY_PACKS,
              COMPANY.AUTHORITIES,
            ],
          },
        ],
      },
      {
        path: 'sales-departments',
        element: <Outlet />,
        restrictedTo: [COMPANY.BATTERY_CELLS, COMPANY.BATTERY_PACKS],
        children: [
          {
            path: '',
            element: <SalesDepartmentsPage />,
            restrictedTo: [COMPANY.BATTERY_CELLS, COMPANY.BATTERY_PACKS],
          },
          {
            path: 'new/:category',
            element: <NewPrivateDocumentPage />,
            restrictedTo: [COMPANY.BATTERY_CELLS, COMPANY.BATTERY_PACKS],
          },
          {
            path: ':id',
            element: (
              <FetchingErrorBoundary>
                <PrivateDocumentDetails />
              </FetchingErrorBoundary>
            ),
            restrictedTo: [COMPANY.BATTERY_CELLS, COMPANY.BATTERY_PACKS],
          },
        ],
      },
      {
        path: 'purchasing-departments',
        element: <Outlet />,
        restrictedTo: [COMPANY.BATTERY_PACKS],
        children: [
          {
            path: '',
            element: <PurchasingDepartmentsPage />,
            restrictedTo: [COMPANY.BATTERY_PACKS],
          },
          {
            path: 'new/:category',
            element: <NewPrivateDocumentPage />,
            restrictedTo: [COMPANY.BATTERY_PACKS],
          },
          {
            path: ':id',
            element: (
              <FetchingErrorBoundary>
                <PrivateDocumentDetails />
              </FetchingErrorBoundary>
            ),
            restrictedTo: [COMPANY.BATTERY_PACKS],
          },
        ],
      },

      // {
      //   path: 'analytics',
      //   element: <AnalyticsPage />,
      //   restrictedTo: allCompanies,
      // },
      // {
      //   path: 'sales',
      //   element: <Outlet />,
      //   restrictedTo: documentRestricted,
      //   children: [
      //     {
      //       path: '',
      //       element: <SalesDepartmentPage />,
      //       restrictedTo: documentRestricted,
      //       children: [
      //         {
      //           path: '',
      //           element: <Navigate to="rfqs-received" />,
      //           restrictedTo: documentRestricted,
      //         },
      //         {
      //           path: 'rfqs-received',
      //           element: <RFQReceived />,
      //           restrictedTo: documentRestricted,
      //         },
      //         {
      //           path: 'quotes-issued',
      //           element: <QuotesIssued />,
      //           restrictedTo: documentRestricted,
      //         },
      //         {
      //           path: 'orders-received',
      //           element: <OrdersReceived />,
      //           restrictedTo: documentRestricted,
      //         },
      //         {
      //           path: 'invoices-issued',
      //           element: <InvoicesIssued />,
      //           restrictedTo: documentRestricted,
      //         },
      //       ],
      //     },
      //     {
      //       path: 'new-quote',
      //       element: <NewQuotePage />,
      //       restrictedTo: documentRestricted,
      //     },
      //     {
      //       path: 'new-invoice',
      //       element: <NewInvoicePage />,
      //       restrictedTo: allCompanies,
      //     },
      //     {
      //       path: 'rfq-received-details/:id',
      //       element: <RFQDetailsPage issued={false} />,
      //       restrictedTo: documentRestricted,
      //     },
      //     {
      //       path: 'quote-issued-details/:id',
      //       element: <QuoteDetailsPage issued={true} />,
      //       restrictedTo: documentRestricted,
      //     },
      //     {
      //       path: 'order-received-details/:id',
      //       element: <OrderDetailsPage issued={false} />,
      //       restrictedTo: documentRestricted,
      //     },
      //     {
      //       path: 'invoice-issued-details/:id',
      //       element: <InvoiceDetailsPage issued={true} />,
      //       restrictedTo: documentRestricted,
      //     },
      //   ],
      // },
      // {
      //   path: 'purchasing',
      //   element: <Outlet />,
      //   restrictedTo: documentRestricted,
      //   children: [
      //     {
      //       path: '',
      //       element: <PurchasingDepartmentPage />,
      //       restrictedTo: documentRestricted,
      //       children: [
      //         {
      //           path: '',
      //           element: <Navigate to="rfqs-issued" />,
      //           restrictedTo: documentRestricted,
      //         },
      //         {
      //           path: 'rfqs-issued',
      //           element: <RFQIssued />,
      //           restrictedTo: documentRestricted,
      //         },
      //         {
      //           path: 'quotes-received',
      //           element: <QuotesReceived />,
      //           restrictedTo: documentRestricted,
      //         },
      //         {
      //           path: 'orders-issued',
      //           element: <OrdersIssued />,
      //           restrictedTo: documentRestricted,
      //         },
      //         {
      //           path: 'invoices-received',
      //           element: <InvoicesReceived />,
      //           restrictedTo: documentRestricted,
      //         },
      //       ],
      //     },
      //     {
      //       path: 'new-rfq',
      //       element: <NewRFQPage />,
      //       restrictedTo: documentRestricted,
      //     },
      //     {
      //       path: 'new-order',
      //       element: <NewOrderPage />,
      //       restrictedTo: documentRestricted,
      //     },
      //     {
      //       path: 'rfq-issued-details/:id',
      //       element: (
      //         <FetchingErrorBoundary>
      //           <RFQDetailsPage issued={true} />
      //         </FetchingErrorBoundary>
      //       ),
      //       restrictedTo: documentRestricted,
      //     },
      //     {
      //       path: 'quote-received-details/:id',
      //       element: (
      //         <FetchingErrorBoundary>
      //           <QuoteDetailsPage issued={false} />
      //         </FetchingErrorBoundary>
      //       ),
      //       restrictedTo: documentRestricted,
      //     },
      //     {
      //       path: 'order-issued-details/:id',
      //       element: (
      //         <FetchingErrorBoundary>
      //           <OrderDetailsPage issued={true} />
      //         </FetchingErrorBoundary>
      //       ),
      //       restrictedTo: documentRestricted,
      //     },
      //     {
      //       path: 'invoice-received-details/:id',
      //       element: (
      //         <FetchingErrorBoundary>
      //           <InvoiceDetailsPage issued={false} />
      //         </FetchingErrorBoundary>
      //       ),
      //       restrictedTo: documentRestricted,
      //     },
      //   ],
      // },
    ],
  },
];
